import axios from 'axios';
import { dataCentralApi } from '@/api/client';
import type { ResponseObject } from '@/types/shared';

/**
 * Generic file upload via the v4 presigned-URL flow: ask the API for a presigned
 * PUT URL, upload the file straight to storage, then return the attachment
 * record the create/update endpoints expect. Mirrors legacy `useUploadFile`.
 */

export interface AttachmentPayload {
  attachment_file_url: string;
  attachment_file_name: string;
  attachment_file_type: string;
  attachment_file_size: number;
}

/** Storage prefix for contract-negotiation attachments (legacy STORGE_PREFIXS). */
export const NEGOTIATION_ATTACHMENT_PREFIX = 'contracts/negotiations';

/** Upload one file and return its attachment record. */
export async function uploadAttachment(
  file: File,
  prefix: string,
  isPublic = false,
): Promise<AttachmentPayload> {
  const key = `${Date.now()}-${file.name}`;
  const res = await dataCentralApi.post<ResponseObject<{ url: string; key: string }>>(
    '/uploads/presigned-url',
    { isPublic, key, prefix },
  );
  const url = res.data.result?.url;
  if (!url) throw new Error('Failed to get a presigned upload URL');
  // PUT straight to storage — a plain request (the presigned URL is pre-authorized;
  // it must NOT carry the app's Authorization header or baseURL).
  await axios.put(url, file, { headers: { 'Content-Type': file.type } });
  return {
    attachment_file_url: `${prefix}/${key}`.replace('//', '/'),
    attachment_file_name: file.name,
    attachment_file_type: file.type,
    attachment_file_size: file.size,
  };
}
